import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Modal,
  Box,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getAllWorkouts, createWorkout } from '../services/workoutsManagementService';
import { useSnackbar } from 'notistack';

const CreateWorkoutButton = ({ onSuccess, disabled, userId }) => {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const allWorkouts = await getAllWorkouts();
        const uniqueCategories = [
          ...new Set(allWorkouts.map((workout) => workout.categoryName).filter(Boolean)),
        ];
        setCategories(uniqueCategories);
      } catch (error) {
        console.error('Error fetching workouts:', error.message);
      }
    };

    fetchCategories();
  }, []);


  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      category: '',
      equipment_needed: false,
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .min(3, 'Workout name must be at least 3 characters')
        .required('Workout name is required'),
      description: Yup.string().required('Workout description is required'),
      category: Yup.string().required('Please select a category'),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        setLoading(true);
        const workoutData = { ...values, user_id: userId };
        await createWorkout(workoutData);
        resetForm();
        setOpen(false);
        onSuccess();
        enqueueSnackbar('Workout created successfully!', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Error creating workout!', { variant: 'error' });
        console.error('Error creating workout:', error.message);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <>
      <Button style={{ margin: '2px' }} variant="contained" color="primary" onClick={() => setOpen(true)} disabled={disabled}>
        Create Workout
      </Button>
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" marginBottom={2}>
            Create a New Workout
          </Typography>
          <Alert severity="info" sx={{ marginBottom: 2 }}>
            Please check if the workout already exists before creating a new one. Note that
            it will require admin approval before being added.
          </Alert>
          <form onSubmit={formik.handleSubmit}>
            <TextField
              label="Workout Name"
              fullWidth
              variant="outlined"
              margin="normal"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              required
            />
            <TextField
              label="Workout Description"
              fullWidth
              variant="outlined"
              margin="normal"
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              multiline
              rows={4}
            />
            <FormControl
              fullWidth
              margin="normal"
              error={formik.touched.category && Boolean(formik.errors.category)}
            >
              <InputLabel>Category</InputLabel>
              <Select
                name="category"
                value={formik.values.category}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                required
              >
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.category && formik.errors.category && (
                <Typography variant="body2" color="error">
                  {formik.errors.category}
                </Typography>
              )}
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  name="equipment_needed"
                  checked={formik.values.equipment_needed}
                  onChange={formik.handleChange}
                />
              }
              label="Equipment Needed"
            />
            <Box display="flex" justifyContent="flex-end" marginTop={2}>
              <Button onClick={() => setOpen(false)} color="secondary" sx={{ marginRight: 2 }}>
                Close
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading || !formik.isValid || !formik.dirty}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Create'}
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>
    </>
  );
};

CreateWorkoutButton.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  userId: PropTypes.number.isRequired
};

export default CreateWorkoutButton;
